import { ComponentRef, WritableSignal, signal } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';

import type { DialogResult } from './dialog.models';

/**
 * The handle returned by DialogService.open() and injected into the frame and
 * the content component. Single source of truth for the result stream and for
 * programmatic close.
 *
 * BINDING CONTRACT - COMMON_COMPONENTS.md section 3.
 */
export class DialogRef<R = unknown> {
  /** The dynamically created content instance. Set by the frame. */
  contentRef: ComponentRef<unknown> | null = null;

  /** Footer spinner / disabled state while the caller performs its own save. */
  readonly busy: WritableSignal<boolean> = signal(false);

  /**
   * Live header text. A signal rather than a plain field because the app is
   * zoneless: setTitle() during an async save must repaint the header.
   */
  readonly title: WritableSignal<string>;

  /**
   * ReplaySubject(1), not Subject: a caller that subscribes after the dialog
   * has already closed still receives the single result and then completion,
   * which is what "emits exactly once, then completes" has to mean for a
   * handle that outlives its own overlay.
   */
  private readonly closed$ = new ReplaySubject<DialogResult<R>>(1);
  private settled = false;

  constructor(
    readonly id: string,
    title = '',
    private readonly onSettled?: (ref: DialogRef<R>) => void
  ) {
    this.title = signal(title);
  }

  /** True until close() has been honoured once. */
  get isOpen(): boolean {
    return !this.settled;
  }

  /** Emits EXACTLY ONCE, then COMPLETES - no takeUntil needed at any call site. */
  afterClosed(): Observable<DialogResult<R>> {
    return this.closed$.asObservable();
  }

  /**
   * Idempotent. The second call is a no-op, which is what guards the
   * Esc-plus-button race: both paths can fire in the same task and only the
   * first result is ever published.
   */
  close(result: DialogResult<R> = { action: 'close' }): void {
    if (this.settled) return;
    this.settled = true;
    this.busy.set(false);
    this.closed$.next(result);
    this.closed$.complete();
    this.contentRef = null;
    this.onSettled?.(this);
  }

  setTitle(title: string): void {
    this.title.set(title);
  }

  setBusy(busy: boolean): void {
    this.busy.set(busy);
  }
}
